import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CareerThroughLine, type ThroughLineEra, type ThroughLineGold } from '../components/CareerThroughLine';
import { FacetChip } from '../components/FacetChip';
import { PhaseRail } from '../components/PhaseRail';
import { SynthesisPanel } from '../components/SynthesisPanel';
import { VoiceCapture } from '../components/VoiceCapture';
import { VoiceTextarea } from '../components/VoiceTextarea';
import { type CorpusReadiness, getCorpusReadiness } from '../lib/api';
import { useDna } from '../lib/useDna';
import { useDnaOverlay } from '../lib/useDnaOverlay';
import { useVoiceAnswer } from '../lib/useVoiceAnswer';
import styles from './Interview.module.css';

// =============================================================================
// Interview — the turn-based DNA excavation screen (LANE DNA-BUILD, Phase 1 +
// Phase 2), at its own route `/refine/interview`, reached via a card on
// Refine.tsx. NOT a 6th tab, NOT folded into Refine's live-agent mic (a
// DIFFERENT feature — the real-time WebRTC agent, useLivingConversation) —
// per the plan's own reasoning (docs/design/dna-interview-port-2026-07-05.md
// §2): collapsing the two doors into one control would recreate the exact
// "which one is this" trust confusion the terminology row already flags.
//
// Ports apps/web/src/pages/Dna.tsx's turn-based flow (Origins → Timeline →
// Synthesis): the phase rail, the visible question, voice-or-text answer
// capture, the running "Captured so far" facet list, and the synthesis
// reveal — plus Phase 2's CareerThroughLine, shown once ≥1 era exists,
// exactly as apps/web gates it (Dna.tsx:247, "state.eras.length > 0").
//
// Deferred to Phase 3 (not built here): TTS question playback (the "Listen"
// button + AudioPlayer/VoicePicker), the career-dna.md export link,
// terminology-alignment polish. The question renders as visible text only,
// same as apps/web's own "stays on screen for the visual reader" line.
//
// NO INVENTION: every render here is either state this hook fetched from the
// server (state.currentQuestion, state.facets, state.profile) or the user's
// own just-submitted text — nothing on this screen invents a question,
// fabricates a facet, or guesses at a synthesis field. User-confirmed
// capture only: an answer is banked exactly when the user taps submit,
// never auto-applied (corpus-entry law).
// =============================================================================

export function Interview() {
  const { state, start, submit, runSynthesis } = useDna();
  const recorder = useVoiceAnswer(state.session?.id ?? null);
  const overlay = useDnaOverlay();
  const [textAnswer, setTextAnswer] = useState('');
  const [readiness, setReadiness] = useState<CorpusReadiness | null>(null);

  useEffect(() => {
    if (state.loading) return;
    let cancelled = false;
    getCorpusReadiness()
      .then((r) => {
        if (!cancelled) setReadiness(r);
      })
      .catch(() => {
        if (!cancelled) setReadiness(null);
      });
    return () => {
      cancelled = true;
    };
  }, [state.loading]);

  const onStart = useCallback(async () => {
    await start();
  }, [start]);

  const onMicToggle = useCallback(async () => {
    if (recorder.state === 'recording') {
      const result = await recorder.stopAndTranscribe();
      if (result?.text) {
        await submit({
          text: result.text,
          source: 'voice',
          capturedAt: new Date().toISOString(),
          meta: result.r2Key ? { r2Key: result.r2Key } : undefined,
        });
      }
    } else {
      await recorder.start();
    }
  }, [recorder, submit]);

  const onSubmitText = useCallback(async () => {
    const text = textAnswer.trim();
    if (!text) return;
    setTextAnswer('');
    await submit({ text, source: 'text', capturedAt: new Date().toISOString() });
  }, [textAnswer, submit]);

  // Map the LIVE data → the through-line viz props. Pure transform of served
  // values (no recompute of the DNA itself) — mirrors apps/web's
  // throughLineEras memo exactly (Dna.tsx:187-208). NO INVENTION on `origin`:
  // an era the interview actually touched (it has DNA facets in the overlay)
  // reads 'captured'; an era with no facets yet reads 'imported' (the
  // resume-filled, not-yet-excavated case) — derived from real signal, never
  // a fabricated provenance flag.
  const facetsByRole = overlay.state.overlay?.facetsByRole ?? {};
  const throughLineEras: ThroughLineEra[] = state.eras.map((era) => {
    const roleFacets = era.roleId ? (facetsByRole[era.roleId] ?? []) : [];
    const gold: ThroughLineGold[] = roleFacets
      .filter((f) => f.isNormalToMe === 1)
      .map((f) => ({ id: f.id, label: f.content }));
    const period = era.startDate?.slice(0, 4);
    const sublabel =
      era.startDate || era.endDate
        ? `${period ?? ''}${era.endDate ? ` – ${era.endDate.slice(0, 4)}` : era.startDate ? ' – now' : ''}`
        : era.company;
    return {
      id: era.roleId ?? era.label,
      label: era.label,
      sublabel: sublabel || undefined,
      period,
      gold,
      origin: roleFacets.length > 0 ? ('captured' as const) : ('imported' as const),
    };
  });

  if (state.loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Reading where you left off…</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Link to="/refine" className={styles.backButton}>
        <svg width="9" height="16" viewBox="0 0 9 16" fill="none" aria-hidden="true">
          <path d="M8 1L2 8l6 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Back to Refine
      </Link>

      <div className={styles.eyebrow}>
        <div className={styles.kicker}>Answer a few questions</div>
        <div className={styles.title}>Building your through-line</div>
      </div>

      {throughLineEras.length > 0 ? (
        <div className={styles.timelineSlot}>
          <CareerThroughLine
            eras={throughLineEras}
            throughLine={state.profile?.throughLine ?? null}
            threads={state.profile?.threads ?? []}
            readiness={
              readiness
                ? { score: readiness.score, ready: readiness.readyToTailor, note: readiness.missing[0] }
                : null
            }
          />
        </div>
      ) : null}

      {!state.session ? (
        <StartCard busy={state.busy} onStart={onStart} />
      ) : (
        <div className={styles.stack}>
          <PhaseRail phase={state.phase} />

          <div className={styles.questionCard}>
            <span className={styles.questionEyebrow}>
              {state.phase === 'origins' ? 'Where it starts' : 'The question'}
            </span>
            <p className={styles.questionText}>{state.currentQuestion}</p>
          </div>

          <VoiceCapture
            state={recorder.state}
            prompt={
              recorder.supported
                ? 'Answer out loud, tangents and all.'
                : 'Speech capture is not available in this browser — type your answer below instead.'
            }
            onToggle={onMicToggle}
          />
          {recorder.interim ? <p className={styles.interim}>{recorder.interim}</p> : null}

          <div className={styles.textFallback}>
            <VoiceTextarea
              label="Or type your answer"
              value={textAnswer}
              onChange={setTextAnswer}
              placeholder="Type here, or tap the mic on the textarea to dictate…"
              rows={4}
            />
            <button
              type="button"
              onClick={onSubmitText}
              disabled={state.busy || textAnswer.trim().length === 0}
              className={styles.submitTextButton}
            >
              Submit answer
            </button>
          </div>

          {state.busy ? <div className={styles.busyRow}>Listening for the gold…</div> : null}
          {state.error ? <p className={styles.errorText}>{state.error}</p> : null}

          {state.facets.length > 0 ? (
            <div>
              <h3 className={styles.capturedTitle}>Captured so far · {state.facets.length}</h3>
              <div className={styles.facetList}>
                {state.facets.map((f) => (
                  <FacetChip key={f.id} facet={f} />
                ))}
              </div>
            </div>
          ) : null}

          {state.phase === 'synthesis' || state.phase === 'complete' ? (
            <SynthesisPanel busy={state.busy} profile={state.profile} onReveal={runSynthesis} />
          ) : null}
        </div>
      )}
    </div>
  );
}

function StartCard({ busy, onStart }: { busy: boolean; onStart: () => void }) {
  return (
    <div className={styles.startCard}>
      <div className={styles.startIcon}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2l2.4 7.2H22l-6 4.4 2.3 7.2L12 16.4l-6.3 4.4 2.3-7.2-6-4.4h7.6z" />
        </svg>
      </div>
      <div>
        <h2 className={styles.startTitle}>Let's excavate.</h2>
        <p className={styles.startBody}>
          We go right back to the start and walk forward, era by era. I ask, you talk. It takes as
          long as it takes, and you can stop and resume any time.
        </p>
        <p className={styles.startPermission}>
          We won't care if you drop the f-bomb talking about your job. Just don't do that at your
          next interview.
        </p>
      </div>
      <button type="button" onClick={onStart} disabled={busy} className={styles.startButton}>
        {busy ? 'Starting…' : 'Start the excavation'}
      </button>
    </div>
  );
}
